import { useEffect, useMemo, useState, useRef, useCallback } from "react"

import { useNavigate } from "react-router-dom"

import { ComputeFacetsResponse } from "@vertesia/client"
import { CollectionItem } from "@vertesia/common"
import { ArrowLeft, FileText, ListFilter, Plus, RotateCw, ChevronDown } from "lucide-react"

import { ReportRunIcon } from "@/components/icons/ReportRunIcon"
import { Button } from "@ui/button"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@ui/dropdown-menu"
import { Input } from "@ui/input"
import { Tabs, TabsList, TabsTrigger } from "@ui/tabs"
import { REPORT_FACETS } from "@/constants/facets"
import { useConfigurationCache } from "@hooks/useConfigurationCache"
import { useUserCache } from "@hooks/useUserCache"
import { getOptionCount } from "@/lib/facets"
import { formatPropertyName } from "@/lib/properties"
import { useUserSession } from "@session/UserSession"

const LIMIT = 1000

export function ReportGeneratorDashboardPanel() {
    const navigate = useNavigate()
    const { client, isLoading, user } = useUserSession()
    const { getUserDisplayName, prefetchUsers } = useUserCache(client)
    const { getConfigurationDisplayName, prefetchConfigurations } = useConfigurationCache(client)

    const [tabState, setTabState] = useState<string>("user_reports")
    const [tabTypeState, setTabTypeState] = useState<string>("all_reports")

    const [searchQuery, setSearchQuery] = useState<string>("")
    const [previousRuns, setPreviousRuns] = useState<CollectionItem[]>([])
    const [loading, setLoading] = useState(false)
    const [hasMore, setHasMore] = useState(true)
    const [offset, setOffset] = useState(0)

    const [currentFilter, setCurrentFilter] = useState("main")
    const [options, setOptions] = useState<ComputeFacetsResponse>({})
    const [types, setTypes] = useState<{ _id: string; count: number }[]>([])
    const [match, setMatch] = useState<Record<string, any>>({})
    const [filtersCount, setFiltersCount] = useState<number>(0)

    const [configurationState, setConfigurationState] = useState(false)

    const scrollRef = useRef<HTMLDivElement>(null)
    const observer = useRef<IntersectionObserver>(null)

    const filteredRuns = useMemo(() => {
        if (tabTypeState === "all_reports") {
            return previousRuns
        }

        if (tabTypeState === null || tabTypeState === undefined) {
            return previousRuns.filter((run) =>
                (run as any)?.properties?.configuration === null || (run as any)?.properties?.configuration === undefined
            )
        }

        return previousRuns.filter((run) =>
            (run as any)?.properties?.configuration === tabTypeState
        )
    }, [previousRuns, tabTypeState])

    const getCurrentConfigurationName = () => {
        if (tabTypeState === "all_reports") {
            return `All NFWF Reports (${previousRuns.length})`
        }

        const currentType = types.find(type => type._id === tabTypeState)
        if (currentType) {
            return `${getConfigurationDisplayName(currentType._id)} (${currentType.count})`
        }

        return "Select Configuration"
    }

    const navigateToCreateReport = () => {
        navigate("/report-generator/create")
    }

    const loadPreviousReportGeneration = useCallback(async (reset?: boolean, loadMore?: boolean) => {
        if (!client || loading) {
            return
        }

        setLoading(true)

        try {
            const name = searchQuery.trim()
            const currentOffset = reset ? 0 : (loadMore ? offset : 0)

            const updatedMatch = { ...match }

            if (tabState === "user_reports" && user?.user_id) {
                updatedMatch.created_by = `user:${user.user_id}`
            }

            for (const opt of Object.values(REPORT_FACETS)) {
                if (match.hasOwnProperty(opt.name) && opt.field !== opt.name) {
                    updatedMatch[opt.field] = match[opt.name]
                    delete updatedMatch[opt.name]
                }
            }

            const query = {
                name: name,
                type: "68219c5af3aed394f1ec4969",
                match: updatedMatch
            }

            client.store.collections.computeListFacets({
                facets: REPORT_FACETS,
                query
            }).then((response) => {
                if (response.total) {
                    delete response.total
                }
                setTypes(response.type || [])

                const opt = structuredClone(response)
                if (opt.type) {
                    delete opt.type
                }
                setOptions(opt)

                if (tabTypeState !== "all_reports" && !response.type?.some(t => t._id === tabTypeState)) {
                    setTabTypeState("all_reports")
                }
            })

            const collections = await client.store.collections.search({
                limit: LIMIT,
                offset: currentOffset,
                ...query
            })

            const orderedCollections = collections.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

            const userIds = orderedCollections
                .map(collection => collection.updated_by)
                .filter(Boolean)
            prefetchUsers(userIds)

            const configurationIds = orderedCollections
                .filter(collection => (collection as any)?.properties?.configuration)
                .map(collection => (collection as any)?.properties?.configuration)
                .filter(Boolean)
            prefetchConfigurations(configurationIds)

            if (reset || !loadMore) {
                setPreviousRuns(orderedCollections)
                setOffset(LIMIT)
            } else {
                setPreviousRuns(prev => [...prev, ...orderedCollections])
                setOffset(prev => prev + LIMIT)
            }

            setHasMore(orderedCollections.length === LIMIT)
        } catch (error) {
            console.error("Error loading reports:", error)
        } finally {
            setLoading(false)
        }
    }, [client, loading, searchQuery, offset, match, tabState, user?.user_id, tabTypeState])

    const handleProgramDelete = (programId: string) => {
        setPreviousRuns((prev) => prev.filter((r) => r.id !== programId))
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault()
            setOffset(0)
            loadPreviousReportGeneration(false, false)
        }
    }

    const handleBackToMain = () => {
        setCurrentFilter("main")
    }

    const handleFilterSelect = (event: React.MouseEvent, filter: string) => {
        event.stopPropagation()
        event.preventDefault()
        setCurrentFilter(filter)
    }

    const handleValueSelect = (filter: string, value: string | undefined) => {
        setMatch((prev) => ({
            ...prev,
            [filter]: value
        }))
        setCurrentFilter("main")
    }

    const clearFilters = () => {
        if (currentFilter !== "main") {
            setMatch((prev) => {
                const newMatch = { ...prev }
                delete newMatch[currentFilter]
                return newMatch
            })
        } else {
            setMatch({})
        }
        setCurrentFilter("main")
    }

    const handleTabChange = (newTab: string) => {
        setTabState(newTab)
        setMatch({})
        setCurrentFilter("main")
        setOffset(0)
    }

    const handleConfigurationChange = (configId: string) => {
        setTabTypeState(configId)
    }

    const lastElementRef = useCallback((node: HTMLDivElement) => {
        if (loading) {
            return
        }
        if (observer.current) {
            observer.current.disconnect()
        }

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                loadPreviousReportGeneration(false, true)
            }
        })

        if (node) {
            observer.current.observe(node)
        }
    }, [loading, hasMore, loadPreviousReportGeneration])

    useEffect(() => {
        if (!client || isLoading) {
            return
        }

        const timeoutId = setTimeout(() => {
            setOffset(0)
            loadPreviousReportGeneration(true)
        }, 300)

        return () => clearTimeout(timeoutId)
    }, [searchQuery, client, isLoading])

    useEffect(() => {
        if (!client || isLoading) {
            return
        }

        loadPreviousReportGeneration(true)
    }, [client, isLoading, match, tabState])

    useEffect(() => {
        setFiltersCount(Object.keys(match).length)
    }, [match])

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-gray-300 p-4 bg-gray-50 flex-shrink-0">
                <div className='flex flex-col gap-0'>
                    <span className="text-lg font-medium">Partner Reports</span>
                    <span className="text-sm text-gray-600">This tool creates consistent content for funder and partner reports by using tested AI instructions to generate roll-up data and project summaries. Staff can highlight specific themes and species for focused narratives and metrics.</span>
                </div>
                <Button onClick={navigateToCreateReport}>
                    <Plus className="h-5 w-5" />
                    Create New Report
                </Button>
            </div>
            <div className='flex flex-col flex-1 bg-gray-100 overflow-hidden'>
                <div className='p-4 flex-shrink-0'>
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex flex-row gap-2 w-1/2">
                            <Tabs value={tabState} onValueChange={handleTabChange}>
                                <TabsList className="flex gap-1 p-1 bg-white justify-center items-center border border-gray-300">
                                    <TabsTrigger value="user_reports">My Reports</TabsTrigger>
                                    <TabsTrigger value="all_reports">All NFWF Reports</TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                        <div className='flex flex-row gap-2 w-1/2'>
                            <div className='flex-1'>
                                <Input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onClear={() => setSearchQuery("")}
                                    onKeyDown={handleInputKeyDown}
                                    placeholder="Search Reports by name..."
                                    className="bg-white"
                                    clearable
                                />
                            </div>
                            <Button onClick={() => loadPreviousReportGeneration(true)}>
                                <RotateCw className="h-5 w-5" />
                            </Button>
                            <DropdownMenu open={configurationState} onOpenChange={setConfigurationState}>
                                <DropdownMenuTrigger asChild>
                                    <Button>
                                        <span>{getCurrentConfigurationName()}</span>
                                        <ChevronDown className={`transition-transform duration-200 ${configurationState ? 'rotate-180' : 'rotate-0'}`} />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64">
                                    <DropdownMenuLabel>Report Type</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        onClick={() => handleConfigurationChange("all_reports")}
                                        className={tabTypeState === "all_reports" ? "bg-blue-100 text-blue-800" : ""}
                                    >
                                        <div className='flex flex-row w-full items-center gap-2'>
                                            <span className="flex-1 min-w-0 text-left truncate">
                                                All NFWF Reports
                                            </span>
                                            <span className='shrink-0'>({previousRuns.length})</span>
                                        </div>
                                    </DropdownMenuItem>
                                    {
                                        types.map((type) => (
                                            <DropdownMenuItem
                                                key={type._id}
                                                onClick={() => handleConfigurationChange(type._id)}
                                                className={tabTypeState === type._id ? "bg-blue-100 text-blue-800" : ""}
                                            >
                                                <div className='flex flex-row w-full items-center gap-2'>
                                                    <span className="flex-1 min-w-0 text-left truncate">
                                                        {getConfigurationDisplayName(type._id)}
                                                    </span>
                                                    <span className='shrink-0'>({type.count})</span>
                                                </div>
                                            </DropdownMenuItem>
                                        ))
                                    }
                                </DropdownMenuContent>
                            </DropdownMenu>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button>
                                        <ListFilter className="h-5 w-5" />
                                        <span>Filters</span>
                                        {filtersCount > 0 && <span className="text-xs text-blue-500">({filtersCount})</span>}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64">
                                    {
                                        currentFilter === "main"
                                            ? (
                                                <>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <DropdownMenuLabel>Filters</DropdownMenuLabel>
                                                        <div className="w-[40px]">
                                                            {
                                                                filtersCount !== 0 &&
                                                                (
                                                                    <Button variant="ghost" size="sm" onClick={clearFilters} className="p-1 italic font-light text-blue-400 hover:underline hover:cursor-pointer hover:text-blue-500">
                                                                        clear
                                                                    </Button>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    <DropdownMenuSeparator />
                                                    <div className='flex flex-col gap-1'>
                                                        {
                                                            Object.keys(options).map((filterKey) => {
                                                                if (tabState === "user_reports" && filterKey === "staff") {
                                                                    return null
                                                                }

                                                                return (
                                                                    <DropdownMenuItem key={filterKey} onClick={(event) => handleFilterSelect(event, filterKey)} className={`${Object.keys(match).includes(filterKey) ? "bg-blue-100 text-blue-800" : ""}`}>
                                                                        <div className='flex flex-row w-full items-center gap-2'>
                                                                            <span className="flex-1 min-w-0 text-left truncate capitalize">{formatPropertyName(filterKey)}</span>
                                                                            <span className='shrink-0'>({getOptionCount(filterKey, options)})</span>
                                                                        </div>
                                                                    </DropdownMenuItem>
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <Button variant="ghost" size="sm" onClick={handleBackToMain} className="p-1">
                                                            <ArrowLeft />
                                                        </Button>
                                                        <DropdownMenuLabel className="capitalize">{formatPropertyName(currentFilter)}</DropdownMenuLabel>
                                                        <div className="w-[40px]">
                                                            {
                                                                filtersCount !== 0 &&
                                                                (
                                                                    <Button variant="ghost" size="sm" onClick={clearFilters} className="p-1 italic font-light text-blue-400 hover:underline hover:cursor-pointer hover:text-blue-500">
                                                                        clear
                                                                    </Button>
                                                                )
                                                            }
                                                        </div>
                                                    </div>
                                                    <DropdownMenuSeparator />
                                                    {
                                                        Object.entries(((options as any)[currentFilter])).map(([value, option]) => (
                                                            <DropdownMenuItem key={value} onClick={() => handleValueSelect(currentFilter, (option as any)._id)} className={`${match[currentFilter] === (option as any)._id ? "bg-blue-100 text-blue-800" : ""}`}>
                                                                <div className='flex flex-row w-full items-center gap-2'>
                                                                    <span className="flex-1 min-w-0 text-left truncate">
                                                                        {
                                                                            (currentFilter === "updated_by" || currentFilter === "created_by" || currentFilter === "staff")
                                                                                ? getUserDisplayName((option as any)._id, undefined)
                                                                                : (
                                                                                    currentFilter === "type"
                                                                                        ? getConfigurationDisplayName((option as any)._id || value)
                                                                                        : (option as any)._id || value
                                                                                )
                                                                        }
                                                                    </span>
                                                                    <span className='shrink-0'>({(option as any).count})</span>
                                                                </div>
                                                            </DropdownMenuItem>
                                                        ))
                                                    }
                                                </>
                                            )
                                    }
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto px-4" ref={scrollRef}>
                    {
                        previousRuns.length === 0 && !loading
                            ? (
                                <div className="flex flex-col gap-6 container mx-auto items-center justify-center h-full">
                                    <div className="flex flex-col gap-6 max-w-4xl mx-auto">
                                        <div className="flex flex-col gap-8 rounded-lg border text-card-foreground text-center py-16 px-8 bg-white">
                                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                                <FileText className="h-12 w-12 text-gray-600" />
                                            </div>
                                            <div className='flex flex-col items-center gap-4'>
                                                <h2 className="text-2xl font-semibold text-gray-900">
                                                    {searchQuery ? "No Reports Found" : tabState === "user_reports" ? "Create Your First Report" : "No Reports Available"}
                                                </h2>
                                                <div>
                                                    <p className="text-gray-600 max-w-lg mx-auto">
                                                        {
                                                            searchQuery
                                                                ? "Try adjusting your search terms or clear the search to see all reports."
                                                                : tabState === "user_reports"
                                                                    ? "Get started by creating a professional report."
                                                                    : "No reports have been created yet."
                                                        }
                                                    </p>
                                                    {
                                                        !searchQuery && tabState === "user_reports" && (
                                                            <p className="text-gray-600 max-w-lg mx-auto">Select documents, choose a template, and let AI generate your report.</p>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        {
                                            !searchQuery && tabState === "user_reports" && (
                                                <div className="grid md:grid-cols-3 gap-6">
                                                    <div className="flex flex-col gap-4 rounded-lg border text-card-foreground bg-white p-6 text-center bg-gray-50 border-gray-200">
                                                        <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center mx-auto">
                                                            <span className="text-white font-bold text-lg">1</span>
                                                        </div>
                                                        <div className='flex flex-col gap-2'>
                                                            <h3 className="font-semibold text-gray-900 mb-2">Create Report</h3>
                                                            <p className="text-sm text-gray-600">Start by giving your report a clear name and description for your intended audience.</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-4 rounded-lg border text-card-foreground bg-white p-6 text-center bg-gray-50 border-gray-200">
                                                        <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center mx-auto">
                                                            <span className="text-white font-bold text-lg">2</span>
                                                        </div>
                                                        <div className='flex flex-col gap-2'>
                                                            <h3 className="font-semibold text-gray-900">Select Documents</h3>
                                                            <p className="text-sm text-gray-600">Search and choose relevant documents from the DAM system to include in your report.</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-4 rounded-lg border text-card-foreground bg-white p-6 text-center bg-gray-50 border-gray-200">
                                                        <div className="w-12 h-12 bg-gray-600 rounded-lg flex items-center justify-center mx-auto">
                                                            <span className="text-white font-bold text-lg">3</span>
                                                        </div>
                                                        <div className='flex flex-col gap-2'>
                                                            <h3 className="font-semibold text-gray-900 mb-2">Choose Template</h3>
                                                            <p className="text-sm text-gray-600">Pick from 10 professional templates designed for different audiences and purposes.</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-4">
                                    {
                                        filteredRuns.map((run, index) => (
                                            <div key={run.id} ref={index === previousRuns.length - 1 ? lastElementRef : null}>
                                                <ReportRunIcon report={run} onDelete={handleProgramDelete} onClose={() => loadPreviousReportGeneration(true)} />
                                            </div>
                                        ))
                                    }
                                    {
                                        loading && (
                                            <div className="flex justify-center py-4">
                                                <div className="flex items-center gap-2 text-gray-500">
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                                                    Loading more reports...
                                                </div>
                                            </div>
                                        )
                                    }
                                    {
                                        !hasMore && previousRuns.length > 0 && (
                                            <div className="flex justify-center py-4">
                                                <span className="text-gray-500 text-sm">No more reports to load</span>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                    }
                </div>
            </div>
        </div>
    )
}